import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Bundle, DiagnosticReport, ImagingStudy, Observation, Organization, Patient, Practitioner, Resource, Specimen, Provenance } from 'fhir/r4';
import { imagingStudyExtensionsToImages, ImagingStudyLocalImage } from './imaging-study-images/imaging-study-images.util';
import { AuthService } from './auth/auth.service';
import { PdfService } from './pdf.service';
import { ReferenceDisplayService } from './reference-display.service';
import { UtilityService } from './utility.service';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { LoadingUIEnabled } from '../loading.interceptor';
import { referenceMatches } from './fhir-reference.util';

type DiagnosticReportContext = {
    report: DiagnosticReport | null;
    patient: Patient | null;
    performer: Resource | null;
    interpreter: Resource | null;
    organization: Organization | null;
    observations: Observation[];
    specimens: Specimen[];
    provenances?: Provenance[];
    imagingStudies: ImagingStudy[];
    patientHospitalId: string | null;
};

@Injectable({ providedIn: 'root' })
export class DiagnosticReportPrintService {
    private auth = inject(AuthService);
    private pdf = inject(PdfService);
    private refDisplay = inject(ReferenceDisplayService);
    private utility = inject(UtilityService);
    private http = inject(HttpClient);
    private backend = inject(backendEndPointToken);
    private terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private organizationTenantTagSystem = `${this.terminologyBaseUrl}/CodeSystem/organizationTenant`;
    private organizationTenantExtensionUrl = `${this.terminologyBaseUrl}/StructureDefinition/organizationTenant`;
    private legacyHospitalExtensionUrl = 'hospitalId';
    private actorOrganizationExtensionUrl = 'http://e-likita.org/fhir/StructureDefinition/actor-organization';

    async printDiagnosticReport(dr: DiagnosticReport, options?: { title?: string }): Promise<void> {
        const hospitalId = (this.auth.user.getValue() as any)?.hospitalId || '';
        const context = await this.hydrateDiagnosticReport(dr);

        const patientDetails = this.buildPatientDetails(
            context.patient,
            (dr.subject as any)?.display || (dr.subject as any)?.reference || '',
            context.patientHospitalId
        );
        const organizationDetails = await this.buildOrganizationDetails(context.organization || null, hospitalId);
        const reportPerformerDisplay = (context.report?.performer?.[0] as any)?.display || (dr.performer?.[0] as any)?.display || '';
        const reportInterpreterDisplay = (context.report?.resultsInterpreter?.[0] as any)?.display || (dr.resultsInterpreter?.[0] as any)?.display || '';
        const performerDetails = this.buildResourceDetails(context.performer || null, reportPerformerDisplay);
        const interpreterDetails = this.buildResourceDetails(context.interpreter || null, reportInterpreterDisplay);

        const title = options?.title || this.codeableConceptToText(dr.code) || 'Diagnostic Report';
        const issued = dr.issued ? new Date(dr.issued).toLocaleString() : '';
        const status = this.titleCase(dr.status || '');

        const observationRows = context.observations.map(o => {
            const test = this.codeableConceptToText(o.code);
            const result = this.formatObservationValue(o);
            const range = this.formatReferenceRange(o);
            const flag = this.codeableConceptArrayToText(o.interpretation);
            return [test, result, range, flag, this.titleCase(o.status || '')];
        });

        const observationTable = observationRows.length
            ? this.buildGenericTable('RESULTS', ['*', 100, 100, 60, 60], [['Test', 'Result', 'Ref. Range', 'Flag', 'Status'], ...observationRows], { headerRows: 1 })
            : null;

        // Build a specimens table (ID, Type, Collected, Received, Status)
        const specimenRows = (context.specimens || []).map(s => {
            const id = s.id || (s.identifier?.[0]?.value) || '';
            const type = this.codeableConceptToText(s.type) || '';
            const collected = s.collection?.collectedDateTime ? new Date(s.collection.collectedDateTime).toLocaleString() : (s.collection?.collectedPeriod ? `${s.collection.collectedPeriod.start || ''}` : '');
            const received = s.receivedTime ? new Date(s.receivedTime).toLocaleString() : '';
            const status = (s.status || '') as string;
            return [id, type, collected, received, this.titleCase(status || '')];
        });
        const specimenTable = specimenRows.length
            ? this.buildGenericTable('SPECIMENS', [80, '*', 120, 120, 60], [['ID', 'Type', 'Collected', 'Received', 'Status'], ...specimenRows], { headerRows: 1 })
            : null;

        const imagingSummary = context.imagingStudies.map(img => {
            const mods = (img.modality || []).map(m => m.display || m.code).filter(Boolean).join(', ');
            return `${img.description || 'Imaging Study'}${mods ? ` [${mods}]` : ''}`;
        }).join('\n');

        // Prepare image blocks by extracting extensions or fetching file links when necessary
        const imagingImageBlocks: any[] = [];
        for (const study of context.imagingStudies || []) {
            const images = imagingStudyExtensionsToImages((study as any)?.extension || []);
            for (const im of images) {
                // If base64/dataUrl available, use directly
                if (im.dataUrl) {
                    imagingImageBlocks.push({ text: im.fileName || '', style: 'detailKey' });
                    imagingImageBlocks.push({ image: im.dataUrl, width: 360, margin: [0, 6, 0, 14] });
                    continue;
                }

                // If fileLink is present, attempt to fetch binary and convert to data URL
                if (im.fileLink) {
                    try {
                        const fetched = await firstValueFrom(this.http.get(im.fileLink, { responseType: 'blob' as 'json', context: new HttpContext().set(LoadingUIEnabled, false) } as any));
                        const blob = (fetched as unknown) as Blob;
                        const dataUrl = await this.blobToDataUrl(blob);
                        imagingImageBlocks.push({ text: im.fileName || '', style: 'detailKey' });
                        imagingImageBlocks.push({ image: dataUrl, width: 360, margin: [0, 6, 0, 14] });
                        continue;
                    } catch {
                        // ignore fetch errors and skip embedding this image
                    }
                }
            }
        }

        const signatureName = performerDetails.name || interpreterDetails.name;
        const signatureSubtitle = performerDetails.subtitle || interpreterDetails.subtitle;
        const signatureBlock = this.buildSignatureBlock(signatureName, null, signatureSubtitle);

        // Build provenance signature blocks (one per Provenance.agent who performed/signature)
        const provenanceSignatureBlocks: any[] = [];
        if (context.provenances && context.provenances.length) {
            for (const prov of context.provenances) {
                const agent = Array.isArray((prov as any).agent) ? (prov as any).agent[0] : null;
                let signerName = '';
                let signerRole = '';
                if (agent) {
                    signerName = agent.who?.display || agent.who?.reference || '';
                    signerRole = agent.role?.[0]?.text || agent.type?.text || '';
                }
                if (signerName) provenanceSignatureBlocks.push(this.buildSignatureBlock(signerName, null, signerRole || undefined));
            }
        }

        const docDefinition = this.buildModernRequestDocument({
            headingText: title,
            org: organizationDetails,
            patient: patientDetails,
            requester: performerDetails, // Using performer as requester for header consistency
            request: {
                reason: dr.conclusion || 'N/A',
                service: `${this.codeableConceptToText(dr.code)}\nStatus: ${status}\nIssued: ${issued}`,
                notes: dr.conclusionCode ? `Conclusion Codes: ${this.codeableConceptArrayToText(dr.conclusionCode)}` : 'None'
            },
            additionalContent: [
                observationTable,
                specimenTable,
                imagingSummary ? this.buildModernSection('IMAGING STUDIES', imagingSummary) : null,
                imagingImageBlocks.length ? { stack: [{ text: 'IMAGES', style: 'sectionHeader', fillColor: '#eeeeee', margin: [0, 0, 0, 5] }, ...imagingImageBlocks] } : null
            ].filter(Boolean),
            postContent: [signatureBlock, ...provenanceSignatureBlocks].filter(Boolean),
            sectionTitles: {
                reason: 'CONCLUSION / FINDINGS',
                service: 'REPORT DETAILS',
                notes: 'ADDITIONAL NOTES'
            }
        });

        this.pdf.print(docDefinition);
    }

    private async hydrateDiagnosticReport(dr: DiagnosticReport): Promise<DiagnosticReportContext> {
        const id = dr.id;
        if (!id) {
            return { report: dr, patient: null, performer: null, interpreter: null, organization: null, observations: [], specimens: [], imagingStudies: [], patientHospitalId: null };
        }

        const queryParts = [
            `_id=${encodeURIComponent(id)}`,
            '_include=DiagnosticReport:subject',
            '_include=DiagnosticReport:performer',
            '_include=DiagnosticReport:result',
            '_include=DiagnosticReport:specimen',
            // '_include=DiagnosticReport:imagingStudy'
            
        ];
        const url = `${this.backend}/DiagnosticReport?${queryParts.join('&')}`;

        try {
            const bundle = await firstValueFrom(this.http.get<Bundle>(url, { context: new HttpContext().set(LoadingUIEnabled, false) }));
            const resourceMap = this.buildResourceMap(bundle?.entry);
            const latest = this.findResourceByReference<DiagnosticReport>(resourceMap, `DiagnosticReport/${id}`) || dr;

            const patient = this.findResourceByReference<Patient>(resourceMap, latest.subject?.reference) || null;
            // keep original performer resource resolved from the report (may be Practitioner/Organization)
            const originalPerformer = this.findResourceByReference<Resource>(resourceMap, latest.performer?.[0]?.reference) || null;
            // try to resolve interpreter from resultsInterpreter (may be missing on some servers)
            let interpreter = this.findResourceByReference<Resource>(resourceMap, latest.resultsInterpreter?.[0]?.reference) || null;
            // If interpreter is not provided by the server, fall back to the report's performer reference
            if (!interpreter && originalPerformer) {
                interpreter = originalPerformer;
            }

            // Default performer: prefer the current user's Organization (hospitalId) if available.
            // If not found, fall back to whatever the report originally referenced.
            let performer: Resource | null = originalPerformer;
            try {
                const currentUser = (this.auth.user.getValue() as any) || {};
                const userOrgId = currentUser?.hospitalId || currentUser?.organizationId || null;
                if (userOrgId) {
                    const org = await this.fetchOrganizationById(userOrgId);
                    if (org) {
                        performer = org;
                    }
                }
            } catch {
                // ignore errors and keep original performer
            }

            const observations = (latest.result || [])
                .map(ref => this.findResourceByReference<Observation>(resourceMap, ref.reference))
                .filter((o): o is Observation => !!o);

            const specimens = (latest.specimen || [])
                .map(ref => this.findResourceByReference<Specimen>(resourceMap, ref.reference))
                .filter((s): s is Specimen => !!s);

            // ImagingStudy _include is not always supported by some servers. Try to resolve
            // imagingStudy references from the bundle first, then fetch missing studies individually.
            let imagingStudies: ImagingStudy[] = ((latest as any).imagingStudy || [])
                .map((ref: any) => this.findResourceByReference<ImagingStudy>(resourceMap, ref.reference))
                .filter((img: any): img is ImagingStudy => !!img);

            const imagingRefs = ((latest as any).imagingStudy || []).map((r: any) => r?.reference).filter(Boolean) as string[];
            const missing = imagingRefs.filter(ref => !resourceMap.has(ref));
            if (missing.length) {
                try {
                    const fetches = missing.map(ref => {
                        const id = (ref || '').toString().split('/').pop();
                        return firstValueFrom(this.http.get<ImagingStudy>(`${this.backend}/ImagingStudy/${encodeURIComponent(id ?? '')}`, { context: new HttpContext().set(LoadingUIEnabled, false) }).pipe(catchError(() => of(null as any))));
                    });
                    const results = await Promise.all(fetches);
                    imagingStudies = imagingStudies.concat((results.filter(Boolean) as ImagingStudy[]));
                } catch {
                    // ignore fetch errors and continue without imaging studies
                }
            }

            let organization = this.resolveOrganizationResource(resourceMap);
            if (!organization) {
                const orgId = this.getOrganizationTenantId(latest as any);
                if (orgId) organization = await this.fetchOrganizationById(orgId);
            }

            // Fetch Provenance records that target this DiagnosticReport (to extract signature lines)
            let provenances: Provenance[] = [];
            try {
                const provUrl = `${this.backend}/Provenance?target=DiagnosticReport/${encodeURIComponent(id || '')}&_count=50`;
                const provBundle = await firstValueFrom(this.http.get<Bundle>(provUrl, { context: new HttpContext().set(LoadingUIEnabled, false) }).pipe(catchError(() => of(null as any))));
                provenances = (provBundle?.entry || []).map((e: any) => e.resource).filter(Boolean) as Provenance[];
            } catch {
                // ignore provenance fetch errors
            }

            return {
                report: latest,
                patient,
                performer,
                interpreter,
                organization,
                observations,
                specimens,
                provenances,
                imagingStudies,
                patientHospitalId: this.extractHospitalIdentifierFromPatient(patient)
            };
        } catch {
            return { report: dr, patient: null, performer: null, interpreter: null, organization: null, observations: [], specimens: [], imagingStudies: [], patientHospitalId: null };
        }
    }

    // --- Modern UI & PDF Helpers (Shared Patterns) ---

    private buildModernRequestDocument(config: any) {
        const { headingText, org, patient, requester, request, additionalContent = [], postContent = [], sectionTitles } = config;
        const content: any[] = [];

        if (org) content.push(this.buildModernOrgHeader(org));
        if (headingText) content.push({ text: headingText, style: 'header', marginBottom: 6 });

        content.push(this.buildModernSeparator());
        content.push(this.buildModernInfoSection(patient, requester));

        content.push(this.buildModernSection(sectionTitles?.reason || 'REASON', request.reason));
        content.push(this.buildModernSection(sectionTitles?.service || 'SERVICE', request.service));

        additionalContent.forEach((block: any) => content.push(block));

        content.push(this.buildModernSection(sectionTitles?.notes || 'NOTES', request.notes));
        postContent.forEach((block: any) => content.push(block));

        content.push({ text: `Generated: ${new Date().toLocaleString()}`, style: 'footerNote', margin: [0, 24, 0, 0], alignment: 'right' });

        return {
            pageSize: 'A4',
            pageMargins: [32, 40, 32, 40],
            content,
            styles: {
                header: { fontSize: 18, bold: true, color: '#2c3e50' },
                subheader: { fontSize: 10, color: '#7f8c8d' },
                label: { fontSize: 8, bold: true, color: '#95a5a6', marginBottom: 2 },
                nameValue: { fontSize: 12, bold: true, marginBottom: 2 },
                smallValue: { fontSize: 10, marginBottom: 2 },
                detailKey: { fontSize: 11, bold: true, color: '#2c3e50' },
                detailValue: { fontSize: 11, color: '#5f6a6a' },
                sectionHeader: { fontSize: 10, bold: true, color: '#2c3e50' },
                bodyText: { fontSize: 11, lineHeight: 1.2 },
                tableTitle: { fontSize: 10, bold: true, color: '#2c3e50', margin: [0, 16, 0, 6] },
                footerNote: { fontSize: 9, color: '#7f8c8d' },
                signatureName: { fontSize: 11, bold: true },
                signatureRole: { fontSize: 10, color: '#555' }
            },
            defaultStyle: { font: 'Roboto', fontSize: 11 }
        };
    }

    private buildModernOrgHeader(details: any) {
        const rightStack = [
            details.name ? { text: details.name, style: 'header', alignment: 'right' } : null,
            details.address ? { text: details.address, style: 'subheader', alignment: 'right', opacity: 0.6 } : null,
            details.telecom ? { text: details.telecom, style: 'subheader', alignment: 'right', opacity: 0.6 } : null,
            details.hospitalId ? { text: `Hospital ID: ${details.hospitalId}`, style: 'subheader', alignment: 'right', opacity: 0.6 } : null
        ].filter(Boolean);

        return {
            columns: [
                details.logo ? { image: details.logo, width: 60, alignment: 'left' } : { width: 60, text: '' },
                { stack: rightStack.length ? rightStack : [{ text: '', alignment: 'right' }] }
            ],
            marginBottom: 10
        };
    }

    private buildModernSeparator() {
        return { canvas: [{ type: 'line', x1: 0, y1: 5, x2: 515, y2: 5, lineWidth: 1 }], marginBottom: 20 };
    }

    private buildModernInfoSection(patient: any, requester: any) {
        return {
            columns: [
                {
                    width: 'auto',
                    table: {
                        widths: [200],
                        body: [[{
                            stack: [
                                { text: 'PATIENT DETAILS', style: 'label' },
                                { text: patient.name || 'N/A', style: 'nameValue' },
                                patient.gender ? { text: `Gender: ${patient.gender}`, style: 'smallValue' } : null,
                                patient.age ? { text: `Age: ${patient.age}`, style: 'smallValue' } : null,
                                patient.hospitalId ? { text: `Hospital ID: ${patient.hospitalId}`, style: 'smallValue' } : null
                            ].filter(Boolean),
                            fillColor: '#f9f9f9',
                            margin: [10, 10, 10, 10]
                        }]]
                    },
                    layout: 'lightHorizontalLines'
                },
                {
                    width: '*',
                    stack: [
                        { text: 'REPORTED BY / INTERPRETED BY', style: 'label', alignment: 'right' },
                        { text: requester.name || 'N/A', style: 'nameValue', alignment: 'right' },
                        requester.subtitle ? { text: requester.subtitle, style: 'smallValue', alignment: 'right' } : null,
                        requester.telecom ? { text: requester.telecom, style: 'smallValue', alignment: 'right' } : null
                    ].filter(Boolean),
                    marginLeft: 20
                }
            ],
            marginBottom: 30
        };
    }

    private buildModernSection(title: string, value: string) {
        return {
            stack: [
                { text: title, style: 'sectionHeader', fillColor: '#eeeeee', margin: [0, 0, 0, 5] },
                { text: value || 'N/A', style: 'bodyText', margin: [0, 0, 0, 20] }
            ]
        };
    }

    private buildGenericTable(title: string, widths: any[], body: any[][], options?: any) {
        return {
            stack: [
                { text: title, style: 'tableTitle' },
                { table: { headerRows: options?.headerRows ?? 0, widths, body }, layout: 'lightHorizontalLines' }
            ]
        };
    }

    private buildSignatureBlock(name?: string, signature?: string | null, subtitle?: string | null) {
        if (!name) return null;
        return {
            columns: [
                { width: '*', text: '' },
                {
                    width: 300,
                    stack: [
                        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 300, y2: 0, lineWidth: 0.5 }], margin: [0, 20, 0, 6] },
                        { text: name, style: 'signatureName', alignment: 'right' },
                        subtitle ? { text: subtitle, style: 'signatureRole', alignment: 'right' } : null,
                        { text: 'Signature & Stamp', style: 'signatureRole', margin: [0, 6, 0, 0], alignment: 'right' }
                    ].filter(Boolean)
                }
            ],
            margin: [0, 12, 0, 0]
        };
    }

    // --- FHIR Logic & Extraction ---

    private buildPatientDetails(patient: Patient | null, fallback: string, hospitalId: string | null) {
        if (!patient) return { name: fallback, hospitalId };
        const dob = patient.birthDate || '';
        const age = dob ? this.computeAge(dob) : '';
        return {
            name: this.getPatientName(patient),
            gender: this.titleCase(patient.gender || ''),
            age,
            hospitalId
        };
    }

    private async buildOrganizationDetails(org: Organization | null, hospitalId: string) {
        if (!org) return { name: 'Hospital', hospitalId };
        return {
            name: org.name || '',
            address: org.address?.[0] ? this.formatAddress(org.address[0]) : '',
            telecom: (org.telecom || []).map(t => `${t.system}: ${t.value}`).join(' | '),
            hospitalId
        };
    }

    private buildResourceDetails(res: Resource | null, fallback: string) {
        if (!res) return { name: fallback };
        if (res.resourceType === 'Practitioner') {
            const p = res as Practitioner;
            const specialty = this.codeableConceptArrayToText(p.qualification?.map(q => q.code));
            return { name: this.getPractitionerName(p), subtitle: specialty };
        }
        return { name: (res as any).name || fallback };
    }

    private formatObservationValue(o: Observation): string {
        if (o.valueQuantity) return `${o.valueQuantity.value} ${o.valueQuantity.unit || ''}`.trim();
        return o.valueString || this.codeableConceptToText(o.valueCodeableConcept) || (o.valueBoolean !== undefined ? o.valueBoolean.toString() : '—');
    }

    private formatReferenceRange(o: Observation): string {
        const range = o.referenceRange?.[0];
        if (!range) return '—';
        if (range.text) return range.text;
        const low = range.low ? `${range.low.value}` : '';
        const high = range.high ? `${range.high.value}` : '';
        return `${low} - ${high} ${range.low?.unit || range.high?.unit || ''}`.trim();
    }

    private buildResourceMap(entries?: any[]): Map<string, Resource> {
        const map = new Map<string, Resource>();
        (entries || []).forEach(e => {
            if (e.resource?.id) map.set(`${e.resource.resourceType}/${e.resource.id}`, e.resource);
        });
        return map;
    }

    private findResourceByReference<T extends Resource>(map: Map<string, Resource>, ref?: string): T | null {
        return ref ? (map.get(ref) as T) || null : null;
    }

    private getPatientName(p: Patient): string {
        const n = p.name?.[0];
        return n ? `${(n.given || []).join(' ')} ${n.family || ''}`.trim() : '';
    }

    private getPractitionerName(p: Practitioner): string {
        const n = p.name?.[0];
        return n ? `${(n.given || []).join(' ')} ${n.family || ''}`.trim() : '';
    }

    private computeAge(dob: string): string {
        const d = new Date(dob);
        const age = new Date().getFullYear() - d.getFullYear();
        return `${age} yrs`;
    }

    private extractHospitalIdentifierFromPatient(p: Patient | null): string {
        return p?.identifier?.find(i => (i.type?.coding || []).some(c => c.code === 'patient-hos-id'))?.value || '';
    }

    private formatAddress(addr: any): string {
        return [addr.line?.join(', '), addr.city, addr.state].filter(Boolean).join(', ');
    }

    private codeableConceptToText(cc: any): string {
        if (!cc) return '';
        return cc.text || cc.coding?.[0]?.display || cc.coding?.[0]?.code || '';
    }

    private codeableConceptArrayToText(arr: any): string {
        return (arr || []).map((cc: any) => this.codeableConceptToText(cc)).filter(Boolean).join(', ');
    }

    private blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result as string);
            fr.onerror = (e) => reject(e);
            fr.readAsDataURL(blob);
        });
    }

    private titleCase(s: string): string {
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    }

    private async fetchOrganizationById(id: string): Promise<Organization | null> {
        try {
            return await firstValueFrom(this.http.get<Organization>(`${this.backend}/Organization/${id}`));
        } catch { return null; }
    }

    private getOrganizationTenantId(res: any): string | null {
        return res?.meta?.tag?.find((t: any) => t.system?.includes('organizationTenant'))?.code || null;
    }

    private resolveOrganizationResource(map: Map<string, Resource>): Organization | null {
        for (const res of map.values()) {
            if (res.resourceType === 'Organization') return res as Organization;
        }
        return null;
    }
}
